import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import moment from 'moment';

//Helpers
import { OrderData, convertToMI, convertToKPH, getValueFloat } from 'Core/data/Helpers';
import { GetFormatForMoment } from '../../../../core/hooks/utils';
import { getReadingEngineHours, roundEngineHours } from '../../.globals/helpers';
import {
    InputNumeric,
    IconButton,
    DatePicker,
    FileUpload,
    TextArea,
    Button,
    Modal,
    Form
} from '@gpstrackit/gtc-react-controls';
import { NotificationContainer } from "Components/Notifications";
import { NotificationManager } from 'Components/Notifications';
import Preferences from 'Core/containers/Preferences';
import Checkbox from 'Components/Checkbox';
import Select from 'Components/Select';
import { Grid } from '@mui/material';

//Actions
import {
    maintenanceMCMSCreateMaintenanceServiceUnitLogItem,
    maintenanceMCMSCreateMaintenanceServiceUnitLog,
    maintenanceMCMSSearchMaintenanceServiceItems,
    maintenanceMCMSCreateMaintenanceServiceItem,
    maintenanceMCMSGetMaintenanceServiceUnit,
    maintenanceMCMSCreateEditMaintenanceServiceUnit,
    reset,
} from './redux/slice';

import {
    maintenanceSMSGetPreviousReading,
    uploadAttachment,
    catalogLoad,
} from 'Redux/actions';

//Styles
import './styles.scss';

const CompleteMaintenanceService = (props) => {
    const { vehicle, refresh } = props;

    const { messages } = useIntl();
    const dispatch = useDispatch();
    const formRef = Form.useForm();
    const preferencesRef = useRef();
    const keyPreferences = "maintenance";

    //Redux states
    const {
        maintenanceServiceUnitLogItem,
        saveCompleteServiceResult,
        maintenanceServiceUnit,
        newServiceItemResult,
        serviceUnitId,
        serviceItems,
        open,
        loading,
        error
    } = useSelector(state => state.maintenanceCompleteMaintenanceServiceRedux);

    const { previousReading } = useSelector(state => state.maintenanceScheduleMaintenanceServiceRedux);

    const { serviceDetail} = useSelector((state) => state.maintenanceServiceDetailRedux);
    const { uploadedAttachment, loadingUploadFile } = useSelector(state => state.commomRedux);
    const { user, authenticatedId } = useSelector(state => state.securityRedux);
    const contactsList = useSelector(state => state.catalogsRedux.contacts);

    //Local states
    const [serviceItemsValidation, setServiceItemsValidation] = useState(false);
    const [validateNextEngineHours, setValidateNextEngineHours] = useState(false);
    const [validateEngineHours, setValidateEngineHours] = useState(false);
    const [serviceItemSelected, setServiceItemSelected] = useState([]);
    const [validateOdometer, setValidateOdometer] = useState(false);
    const [checkReschedule, setCheckReschedule] = useState(false);
    const [contactsSelected, setContactsSelected] = useState([]);
    const [optionsSelect, setOptionsSelect] = useState([]);
    const [dataService, setDataService] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [preferences, setPreferences] = useState({});
    const [formData, setFormData] = useState({});
    const [reading, setReading] = useState(null);

    useEffect(() => {
        if (saveCompleteServiceResult?.id && maintenanceServiceUnit?.id) {
            if (serviceItemSelected?.length) {
                const payload = {
                    serviceUnitLogId: saveCompleteServiceResult.id,
                    items: serviceItemSelected,
                    itemsIncluded: formRef?.ref?.current?formRef.ref.current.getValues():{}
                };
                dispatch(maintenanceMCMSCreateMaintenanceServiceUnitLogItem(payload))
            } else {
                correctCreation(messages['completeMaintenanceService_service_completed']);
                refresh && refresh();
                handleClose();
            }
        }
    }, [saveCompleteServiceResult]);

    useEffect(() => {
        if (maintenanceServiceUnitLogItem && maintenanceServiceUnitLogItem?.id) {
            correctCreation(messages['completeMaintenanceService_service_completed']);
            refresh && refresh();
            handleClose();
        }
    }, [maintenanceServiceUnitLogItem]);

    useEffect(() => {
        if (newServiceItemResult?.id) {
            addNewServiceItemToSelect(newServiceItemResult);
            onChangeSelectedItems(newServiceItemResult)
            correctCreation(messages['completeMaintenanceService_service_item_success']);
            dispatch(reset({ newServiceItemResult: null }))
        }
    }, [newServiceItemResult])

    useEffect(() => {
        if (maintenanceServiceUnit?.service) {
            setOptionsSelect(OrderData(serviceItems, 'name'));
        }
    }, [maintenanceServiceUnit?.id]);

    useEffect(() => {
        if (serviceUnitId) dispatch(maintenanceMCMSGetMaintenanceServiceUnit({ id: serviceUnitId }))
    }, [serviceUnitId]);

    useEffect(() => {
        if (maintenanceServiceUnit?.service) {
            setOptionsSelect(OrderData(serviceItems, 'name'));
        }

        if (maintenanceServiceUnit?.files) {
            formRef.setValues({attachments:maintenanceServiceUnit?.files});
            setFormData({...formData,attachments:maintenanceServiceUnit?.files})
        }
    }, [maintenanceServiceUnit])

    useEffect(() => {
        if (vehicle) {
            setDataService(vehicle);
        }
    }, [vehicle, open]);

    const getOffset = (value) => (isNaN(value) ? 0 : getValueFloat(value));

    const getVehicleData = (vehicle, formData) => {
        const {
            useEcuOdometer = 0,
            useEcuEngineHours = 0,
            offsetEcuEngineHours,
            offsetEngineHours,
        } = vehicle || formData?.vehicle || {};

        const offset = useEcuEngineHours
            ? getOffset(offsetEcuEngineHours)
            : getOffset(offsetEngineHours);

        return { useEcuOdometer, useEcuEngineHours, offset };
    };

    useEffect(() => {
        if (previousReading && previousReading?.[0]) {
            const { useEcuOdometer, useEcuEngineHours, offset } = getVehicleData(vehicle, formData);

            const reading = Array.isArray(previousReading) && previousReading.length > 0
                ? previousReading[0]
                : null;

            if (!reading) return;
            //Get offsets from vehicle object to add them to the readings values. Both for odometer and engine hours.
            const engineOdometerOffset = parseFloat(useEcuOdometer ? vehicle?.offsetEcuEngineOdometer : vehicle?.offsetEngineOdometer || 0);
            const defineOdometer = useEcuOdometer
                ? reading?.ecuEngineOdometer + engineOdometerOffset //adding offset
                : reading?.engineOdometer + engineOdometerOffset;   //adding offset

            const readingHours = getReadingEngineHours(reading, useEcuEngineHours);

            let engineOdometer = isNaN(parseFloat(defineOdometer)) ? "" : Math.round(defineOdometer);
            engineOdometer = user.isKph ? convertToKPH(engineOdometer) : engineOdometer;
            let engineHours = "";
            if (readingHours !== null) {
                engineHours = roundEngineHours(readingHours + offset); //adding offset
            }

            setTimeout(() => {
                setFormData(prevFormData => ({ ...prevFormData, engineOdometer, engineHours}));
                formRef.setValues({ engineOdometer, engineHours });
            }, 100);
            setReading({ ...previousReading, tempEngineOdometer: engineOdometer, tempEngineHours: engineHours });
        }
        else if (formData?.date) {
            let tempEngineOdometer = vehicle?.useEcuOdometer
                ? parseFloat(vehicle?.ecuEngineOdometer) + parseFloat(vehicle?.offsetEcuEngineOdometer)
                : parseFloat(vehicle?.engineOdometer) + parseFloat(vehicle?.offsetEngineOdometer);

            const tempEngineHours = vehicle?.useEcuEngineHours
                ? parseFloat(vehicle?.ecuEngineHours) + parseFloat(vehicle?.offsetEcuEngineHours)
                : parseFloat(vehicle?.engineHours) + parseFloat(vehicle?.offsetEngineHours);

            tempEngineOdometer = user.isKph ? convertToKPH(tempEngineOdometer) : tempEngineOdometer;

            setTimeout(() => {
                setFormData(prevFormData => ({ ...prevFormData, engineOdometer: Math.round(Number(tempEngineOdometer)), engineHours: roundEngineHours(tempEngineHours) ?? "" }));
                formRef.setValues({ engineOdometer: Math.round(Number(tempEngineOdometer)), engineHours: roundEngineHours(tempEngineHours) ?? "" });
            }, 100);
        }
    }, [previousReading]);

    useEffect(() => {
        calculateNextOdometer();
        calculateNextEngineHours();
    }, [formData?.engineOdometer, formData?.engineHours]);

    useEffect(() => {
        if (open) {
            setTimeout(() => {
                    formRef.setValues({ nextDueDate: 0 })
            }, 200);
            if (!contactsList.length) dispatch(catalogLoad(['contacts']))
            dispatch(maintenanceMCMSSearchMaintenanceServiceItems({}))
        }
    }, [open]);

    useEffect(() => {
        if (serviceItems && Array.isArray(serviceItems)) {
            setOptionsSelect(OrderData(serviceItems, 'name'));
        }
    }, [serviceItems])

    useEffect(() => {
        if (error) {
            NotificationManager.error(
                error?.error || messages['errorMessage'],
                messages['error'],
                3000,
                null,
                null,
                'error',
            );
            dispatch(reset({ error: null }))
        }
    }, [error]);

    useEffect(() => {
        if (maintenanceServiceUnit) {
            let engineHours = maintenanceServiceUnit?.service?.engineHours > 0
            let miles = maintenanceServiceUnit?.service?.miles > 0
            setValidateOdometer(miles)
            setValidateEngineHours(engineHours)
            if(maintenanceServiceUnit?.serviceUnitItems  && maintenanceServiceUnit?.serviceUnitItems.length){
                setServiceItemSelected(maintenanceServiceUnit?.serviceUnitItems)
                setTimeout(() => {
                    let values={}
                    let newOptions = optionsSelect
                    maintenanceServiceUnit?.serviceUnitItems.forEach((serv, index) => {
                        values[`parts-${index}`]= serv?.parts || '0'
                        values[`labor-${index}`]= serv?.labor || '0'
                        values[`subtotal-${index}`]=serv?.subtotal || '0'
    
                         newOptions = newOptions.filter((option) => option?.id != serv?.id);
                    });
                    newOptions && setOptionsSelect(OrderData(newOptions, 'name'));
                    formRef.setValues(values)
                    getTotalCost(maintenanceServiceUnit?.serviceUnitItems);
                }, 500);
            }else{
                setServiceItemSelected([])
    
            }
        }

    }, [maintenanceServiceUnit])

    useEffect(() => {
        if (formData?.date) {
            if (dataService?.id) {
                getReadings(dataService?.id, formData?.date);
            }
            calculateNextDueDate();
        }
        if(formData?.date && !validateNextEngineHours){
            setValidateNextEngineHours(true)
            calculateNextEngineHours();
        }
    }, [formData?.date]);

    const handleClose = () => {
        setValidateNextEngineHours(false);
        setServiceItemsValidation(false);
        setValidateEngineHours(false);
        setServiceItemSelected([]);
        setValidateOdometer(false);
        setCheckReschedule(false);
        setContactsSelected([]);
        setOptionsSelect([]);
        setDataService(null);
        setErrorMessage("");
        setPreferences({});
        dispatch(reset());
        setReading(null);
        setFormData({});
    }

    const calculateNextEngineHours = () => {
        const newNextEngineHours = roundEngineHours((roundEngineHours(formData?.engineHours) || 0) + (Number(maintenanceServiceUnit?.service?.engineHours) || 0));
        setTimeout(() => {
            formRef?.setValues({ nextEngineHours: newNextEngineHours })
        }, 300);
        setFormData(prevFormData => ({ ...prevFormData, nextEngineHours: newNextEngineHours }))
    };

    const calculateNextOdometer = () => {
        const newNextOdometer = parseFloat((formData?.engineOdometer || 0)) + parseFloat(maintenanceServiceUnit?.service?.miles || 0);
        setTimeout(() => {
            formRef?.setValues({ nextOdometer: newNextOdometer })
        }, 200);
        setFormData(prevFormData => ({ ...prevFormData, nextOdometer: newNextOdometer }))
    };

    const addNewServiceItemToSelect = (serviceItem) => {
        let newServiceItems = JSON.parse(JSON.stringify(serviceItems))
        newServiceItems = [serviceItem, ...newServiceItems];
        setOptionsSelect(OrderData(newServiceItems, 'name'));
    }

    const correctCreation = (message) => {
        NotificationManager.success(
            message,
            messages['success'],
            3000,
            null,
            null,
            'success',
        );
    };

    const addNewOption = (name) => {
        if (name) {
            dispatch(maintenanceMCMSCreateMaintenanceServiceItem({ name }))
        }
    };

    const changeReschedule = () => {
        setCheckReschedule(!checkReschedule);
        savePreferences({ reScheduleNextVehicleMaintenance: !checkReschedule });
    };

    const onChangeItem = (key, name, value) => {
        setServiceItemsValidation(false)
        let newServiceItems = JSON.parse(JSON.stringify(serviceItemSelected));
        let newValue = value;
        if (newValue < 0) {
            newValue = 0;
        }
        if (newServiceItems[key]) {
            let temp = newServiceItems[key];
            temp[name] = +newValue === 0 ? newValue : parseFloat(newValue)
            temp['subtotal'] = parseFloat(temp.parts || 0) + parseFloat(temp.labor || 0);
            newServiceItems[key] = temp;
            setServiceItemSelected(newServiceItems);
            getTotalCost(newServiceItems);
        }
    };

    const reducer = (accumulator, currentValue) => accumulator + currentValue.subtotal;

    const getTotalCost = (newServiceItem) => {
        if (newServiceItem.length) {
            const total = newServiceItem.reduce(reducer, 0);
            setTimeout(() => {
                formRef?.setValues({ totalCost: total })
            }, 100);
        } else {
            setTimeout(() => {
                formRef?.setValues({ totalCost: 0 })
            }, 100);
        }
    };

    const onDelete = (id) => {
        let newServiceItems = JSON.parse(JSON.stringify(serviceItemSelected));
        let newOptionsSelect = JSON.parse(JSON.stringify(optionsSelect));
        const oldOptionSelected = newServiceItems.find((service) => service.id == id);
        newServiceItems = newServiceItems.filter((service) => service.id != id);
        newOptionsSelect.push(oldOptionSelected);
        setOptionsSelect(OrderData(newOptionsSelect, 'name'));
        setServiceItemSelected(newServiceItems);

        newServiceItems.forEach((item, index) => {
            setTimeout(() => {
                formRef.setValues({
                    [`parts-${index}`]: item?.parts,
                    [`labor-${index}`]: item?.labor,
                    [`subtotal-${index}`]: item?.subtotal,
                })
            }, 200);
        });

        getTotalCost(newServiceItems);
    };

    const renderItem = (item, key) => {
        let name = item?.name || ""

        if (name.length > 15) {
            name = name.slice(0, 15) + '...';
        }

        return (
            <Grid container item sm={12} key={key} spacing={0} alignItems="center">
                <Grid item sm={1}>
                    <Form.Item name={`included-${key}`} abel={''}>
                        <Checkbox
                            onChange={(value)=>{
                                formRef.setValues({
                                    [`included-${key}`]: value
                                })
                            }}
                            size="small"
                            id={`included-${key}`}
                        />
                    </Form.Item>
                </Grid>
                <Grid item sm={3} style={{ height: 80 }}>
                    <Grid container direction="column" alignItems="flex-start" justifyContent="center" className="contentLabelItems overflow-break-word">
                        <Grid item container sm={12}>
                            <Grid item sm={12} >
                                <label style={{
                                    color: '#000',
                                    fontSize: 12,
                                    fontWeight: 'bold',
                                    lineHeight: 1.5,
                                    letterSpacing: '.00938em',
                                    alignSelf: 'unset',
                                }}>
                                    {messages['completeMaintenanceService_item']}
                                </label>
                            </Grid>
                        </Grid>
                        <Grid item container className="labelContainer" alignItems="center" justifyContent="center" style={{ marginBottom: 4 }}>
                            <Grid item sm={12} style={{ width: 200 }}>
                                <label className="labelItemService table-span">{name}</label>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item container justifyContent="flex-end" alignItems="center" sm={8}>
                    <Grid item container sm={11} spacing={1}>
                        <Grid item sm={4}>
                            <Form.Item
                                name={`parts-${key}`}
                                label={messages['completeMaintenanceService_parts']}
                                rules={[
                                    {
                                        min: 0,
                                        message: `${messages['min']}: 0`
                                    },
                                    {
                                        max: 999999,
                                        message: `${messages['max']}: 999999`
                                    }
                                ]}
                            >
                                <InputNumeric
                                    id={`parts-${key}`}
                                    type="currency"
                                    onChange={(value) => {
                                        value = value.replace(/[_-]/g, "");
                                        onChangeItem(key, 'parts', parseFloat(value));
                                        setTimeout(() => {
                                            let currentValues = parseFloat(value || 0) + parseFloat(item.labor || 0)
                                            formRef.setValues({
                                                [`subtotal-${key}`]: currentValues
                                            })
                                        }, 200);
                                    }}
                                    placeholder={'$'}
                                    maxLength={9}
                                    decimalPlaces={2}
                                />
                            </Form.Item>
                        </Grid>
                        <Grid item sm={3}>
                            <Form.Item
                                name={`labor-${key}`}
                                label={messages['completeMaintenanceService_labor']}
                                rules={[
                                    {
                                        min: 0,
                                        message: `${messages['min']}: 0`
                                    },
                                    {
                                        max: 999999,
                                        message: `${messages['max']}: 999999`
                                    }
                                ]}
                            >
                                <InputNumeric
                                    id={`labor-${key}`}
                                    type="currency"
                                    onChange={(value) => {
                                        value = value.replace(/[_-]/g, "");
                                        onChangeItem(key, 'labor', parseFloat(value));
                                        setTimeout(() => {
                                            let currentValues = parseFloat(item.parts || 0) + parseFloat(value || 0)
                                            formRef.setValues({
                                                [`subtotal-${key}`]: currentValues
                                            })
                                        }, 200);
                                    }}
                                    placeholder={'$'}
                                    maxLength={9}
                                    decimalPlaces={2}
                                />
                            </Form.Item>
                        </Grid>
                        <Grid item sm={3}>
                            <Form.Item
                                name={`subtotal-${key}`}
                                label="SUBTOTAL"
                            >
                                <InputNumeric
                                    id={`subtotal-${key}`}
                                    type="currency"
                                    onChange={(value) => {
                                        onChangeItem(key, 'labor', value);
                                    }}
                                    placeholder={'$'}
                                    isDisabled
                                    decimalPlaces={2}
                                />
                            </Form.Item>
                        </Grid>
                    </Grid>
                    <Grid item container justifyContent="flex-end" sm={1}>
                        <Grid item sm={3}></Grid>
                        <Grid item sm={9}>
                            <div className="form-item">
                                <label className="input-normal-label"></label>
                                <IconButton
                                    aria-label="placeholder"
                                    className="icon-trash-item"
                                    onClick={() => onDelete(item.id)}
                                    icon="delete"
                                    style={{ padding: 5, fontSize: 20 }}
                                />
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    const calculateNextDueDate = () => {
        if (maintenanceServiceUnit?.service?.days) {
            let newDateDueNext = moment(formData?.date);
            newDateDueNext = newDateDueNext.add(maintenanceServiceUnit.service.days, 'day').format();
            const toDate = new Date(newDateDueNext);
            setTimeout(() => {
                setFormData(prevFormData => ({ ...prevFormData, nextDueDate: toDate }))
                formRef?.setValues({ nextDueDate: toDate })
            }, 100);
        }
    };

    const getReadings = (deviceId, date) => {
        let timestamp = moment(date).unix();
        dispatch(maintenanceSMSGetPreviousReading({
            deviceId: deviceId + '',
            unitTime: {
                start: moment(timestamp * 1000)
                    .subtract(15, 'days')
                    .unix(),
                end: timestamp
            },
        }))
    };

    const onChangeSelectedItems = (value) => {
        setServiceItemSelected((service) => {
            return [
                ...service,
                { ...value, parts: 0, labor: 0, subtotal: 0 },
            ]
        });
        setTimeout(() => {
            formRef.setValues({
                [`parts-${serviceItemSelected?.length}`]: null,
                [`labor-${serviceItemSelected?.length}`]: null,
                [`subtotal-${serviceItemSelected?.length}`]: null,
            })
        }, 100);
        const newOptions = optionsSelect.filter((option) => option?.id != value?.id);
        newOptions && setOptionsSelect(OrderData(newOptions, 'name'));
    };

    const savePreferences = useCallback((newPreferences) => {
        if (authenticatedId) {
            let preferencesToSave = { ...preferences, ...newPreferences }
            setPreferences(preferencesToSave)
            preferencesRef?.current?.savePreference("User", keyPreferences, preferencesToSave)
        }
    });

    const organizedContactsToSave = (contacts) => {
        const contactsToSave = contacts.map((contact) => ({
            value: contact.value,
            type: contact.type,
        }));
        return contactsToSave;
    };

    const onSubmit = () => {
        setErrorMessage("");
        let validation = false
        let itemsResult = []
        if (serviceItemSelected.length > 0) {
            serviceItemSelected.map((item) => {
                const { __typename, ...resultItem } = item;
                itemsResult.push({
                    id:resultItem?.id,
                    parts:resultItem?.parts || 0,
                    labor:resultItem?.labor || 0,
                    subtotal:resultItem?.subtotal || 0,
                    name:resultItem?.name
                })
            })
        }

        if (!validation) {
            const contactsToSave = organizedContactsToSave(contactsSelected);
            if (maintenanceServiceUnit?.id) {
                let fileIds = formData?.attachments ? formData?.attachments.map((item) => item.id) : []
                const payload = {
                    maintenanceDate: formData?.date.toISOString(),
                    completed: !checkReschedule,
                    estimatedCost: maintenanceServiceUnit?.service?.estimatedCost,
                    serviceUnitId: maintenanceServiceUnit.id,
                    readingId: reading?.unitId && `${reading.unitId}_${reading.unitTime}`,
                    notifyToInput: contactsToSave,
                    notes: formData.notes,
                    actualCost: formData?.totalCost,
                    engineHours: roundEngineHours(formData?.engineHours) || 0,
                    odometer: formData?.engineOdometer || 0,
                    nextServiceDate: maintenanceServiceUnit?.service?.days ? formData?.nextDueDate : null,
                    nextServiceOdometer: maintenanceServiceUnit?.service?.miles ? formData?.nextOdometer : 0,
                    nextServiceEngineHours: maintenanceServiceUnit?.service?.engineHours ? (roundEngineHours(formData?.nextEngineHours) || 0) : 0,
                    fileIds: fileIds
                };

                if (!vehicle?.odometerEnable && maintenanceServiceUnit?.service?.miles) {
                    setErrorMessage(messages['scheduleMaintenanceService_notSupportOdometer']);
                } else if (!vehicle?.hoursEnable && maintenanceServiceUnit?.service?.engineHours) {
                    setErrorMessage(messages['scheduleMaintenanceService_notSupportEngine']);
                } else {

                    let subscriberData = []
                    if (maintenanceServiceUnit?.subscribers && maintenanceServiceUnit?.subscribers?.length > 0) {
                        maintenanceServiceUnit?.subscribers?.map((item) => {
                            const { __typename, contact,...resultItemS } = item;
                            subscriberData.push(resultItemS) 
                        })
                    }
                
                    dispatch(maintenanceMCMSCreateEditMaintenanceServiceUnit({
                        ...maintenanceServiceUnit,
                        nextServiceDate: maintenanceServiceUnit?.service?.days ? formData?.nextDueDate : null,
                        nextServiceOdometer: maintenanceServiceUnit?.service?.miles ? formData?.nextOdometer : 0,
                        nextServiceEngineHours: maintenanceServiceUnit?.service?.engineHours ? (roundEngineHours(formData?.nextEngineHours) || 0) : 0,
                        items:itemsResult,
                        subscribers:subscriberData
                    })); 

                    dispatch(maintenanceMCMSCreateMaintenanceServiceUnitLog(payload));
                }
            }
        }
    };

    return (
        <Modal
            title={`${messages['completeMaintenanceService_complete']} ${maintenanceServiceUnit?.service?.title ? '"' + maintenanceServiceUnit.service.title + '"' : ''} ${dataService?.label ? messages['completeMaintenanceService_for'] : ''} ${dataService?.label ? '"' + dataService?.label + '"' : ''}`}
            open={open}
            handleClose={handleClose}
            position="right"
            size="small"
            loading={loading}
            style={{ zIndex: 'auto' }}
            footer={(
                <div>
                    <Button
                        onClick={handleClose}
                        variant="secondary"
                        style={{ marginRight: "5px" }}
                    >
                        {messages['cancel']}
                    </Button>
                    <Button
                        onClick={() => formRef.submit()}
                        variant="primary"
                    >
                        {messages['completeMaintenanceService_max_complete']}
                    </Button>
                </div>
            )}
        >
            <Preferences
                ref={preferencesRef}
                preferencesKey={keyPreferences}
                onInitialPreferences={(preferences) => {
                    setPreferences(preferences);
                    setCheckReschedule(preferences?.reScheduleNextVehicleMaintenance ? true : false);
                }}
            >
                <div style={{ padding: '10px 25px' }}>
                    <Form
                        ref={formRef.ref}
                        onSubmit={(_values) => {
                            if (!_values.date) {
                                return
                            }
                            if (vehicle?.odometerEnable && validateOdometer && !_values.engineOdometer) {
                                return
                            }
                            if (vehicle?.hoursEnable && validateEngineHours && !_values.engineHours) {
                                return
                            }
                            if (checkReschedule) {
                                if (maintenanceServiceUnit?.service?.days && !_values.nextDueDate) {
                                    return
                                }
                                if (maintenanceServiceUnit?.service?.miles && !_values.nextOdometer) {
                                    return
                                }
                                if (maintenanceServiceUnit?.service?.engineHours && !_values.nextEngineHours) {
                                    return
                                }
                            }

                            onSubmit()

                        }}
                    >
                        <Form.Item
                            name="date"
                            label={messages['completeMaintenanceService_date']}
                            rules={[{ required: true, message: messages['required_field'] }]}
                        >
                            <DatePicker
                                id="date"
                                showTimePicker
                                calendarPosition="bottom-end"
                                placeholder={messages['completeMaintenanceService_select_date']}
                                format={GetFormatForMoment(user.dateformat)}
                                maxDate={new Date()}
                                onChange={(value) => {
                                    if (moment().isSame(value, 'days')) {
                                        if (moment(value).isAfter(moment(), 'minutes')) {
                                            NotificationManager.warning(
                                                messages['completeMaintenanceService_wait'],
                                                messages['completeMaintenanceService_invalid_time'],
                                                3000,
                                                null,
                                                null,
                                                'success',
                                            );
                                        }
                                    }
                                    setFormData({ ...formData, date: value })
                                }}
                            />
                        </Form.Item>
                        {
                            vehicle?.odometerEnable ?
                                <Form.Item
                                    label={messages['completeMaintenanceService_max_odometer']}
                                    name="engineOdometer"
                                    rules={[
                                        {
                                            required: validateOdometer,
                                            message: messages['required_field']
                                        },
                                        {
                                            min: validateOdometer && 1,
                                            message: `${messages['min']}: 1`
                                        },
                                        {
                                            max: 1000000000,
                                            message: `${messages['max']}: 1000000000`
                                        }
                                    ]}
                                >
                                    <InputNumeric
                                        id="engineOdometer"
                                        type="number"
                                        placeholder={messages['completeMaintenanceService_odometer']}
                                        maxLength={10}
                                        onChange={(value) => {
                                            if (user.isKph) {
                                                value = convertToMI(value);
                                            }
                                            setFormData({ ...formData, engineOdometer: value });
                                        }}
                                        isDisabled={!formData?.date}
                                        onlyPositive
                                    />
                                </Form.Item>
                                : <></>
                        }
                        {
                            vehicle?.hoursEnable ?
                                <Form.Item
                                    name="engineHours"
                                    label={messages['completeMaintenanceService_max_engine_hours']}
                                    rules={[
                                        {
                                            required: validateEngineHours,
                                            message: messages['required_field']
                                        },
                                        {
                                            min: validateEngineHours && 1,
                                            message: `${messages['min']}: 1`
                                        },
                                        {
                                            max: 1000000000,
                                            message: `${messages['max']}: 1000000000`
                                        }
                                    ]}
                                >
                                    <InputNumeric
                                        id="engineHours"
                                        type="number"
                                        placeholder={messages['completeMaintenanceService_engine_hours']}
                                        maxLength={10}
                                        onChange={(value) => setFormData({ ...formData, engineHours: value })}
                                        isDisabled={!formData.date}
                                        onlyPositive
                                    />
                                </Form.Item>
                                : <></>
                        }
                        <Form.Item
                            name="options"
                            label={messages['completeMaintenanceService_add_service_items']}
                        >
                            <div className="test-options">
                                <Select
                                    addNewOption={addNewOption}
                                    messageAdd={messages['completeMaintenanceService_add_new_service_item']}
                                    onChange={(data) => {
                                        let payload = data
                                        if (payload?.label) {
                                            payload.name = payload?.label
                                        }

                                        if(data?.id)
                                            onChangeSelectedItems(payload)
                                    }}
                                    id="options"
                                    options={optionsSelect}
                                    textField="name"
                                    valueField="id"
                                    customIcon={() => <IconButton size="large" icon="add" style={{ padding: 5 }} />}
                                    placeholder={messages['select']}
                                    maxLength={40}
                                    createOptions
                                    isClearable
                                    isSearchable
                                />
                            </div>

                        </Form.Item>
                        {serviceItemSelected &&
                            serviceItemSelected.map((item, key) => renderItem(item, key))}
                        {serviceItemsValidation && (
                            <Grid item sm={12}>
                                <small className="text-danger">{messages['completeMaintenanceService_error_items']}</small>
                            </Grid>
                        )}
                        <Form.Item
                            name="totalCost"
                            label={messages['completeMaintenanceService_total_cost']}
                        >
                            <InputNumeric
                                id="totalCost"
                                isDisabled
                                placeholder="$0.00"
                                type="currency"
                                decimalPlaces={2}
                            />
                        </Form.Item>
                        {
                            formData?.date && (
                                <>
                                    <div className="checkbox-container">
                                        <Checkbox
                                            value={checkReschedule}
                                            onChange={changeReschedule}
                                            size="small"
                                            id="checkReschedule"
                                            label={messages['completeMaintenanceService_re_schedule_next_maintenance']}
                                        />
                                    </div>
                                    {
                                        checkReschedule &&
                                        <>
                                            {
                                                maintenanceServiceUnit?.service?.days ?
                                                    <>
                                                        <Form.Item
                                                            name="nextDueDate"
                                                            label={messages['completeMaintenanceService_next_due_date']}
                                                            rules={[
                                                                {
                                                                    required: maintenanceServiceUnit?.service?.days,
                                                                    message: messages['required_field']
                                                                }
                                                            ]}
                                                        >
                                                            <DatePicker
                                                                id="nextDueDate"
                                                                showTimePicker
                                                                minDate={new Date(formData?.date)}
                                                                calendarPosition="bottom-end"
                                                                format={GetFormatForMoment(user.dateformat)}
                                                                onChange={(value) => setFormData({ ...formData, nextDueDate: value })}
                                                                placeholder={messages['completeMaintenanceService_select_date']}
                                                            />
                                                        </Form.Item>
                                                    </>
                                                    : <></>
                                            }
                                            {
                                                maintenanceServiceUnit?.service?.miles ?
                                                    <Form.Item
                                                        name="nextOdometer"
                                                        label={messages['completeMaintenanceService_next_odometer']}
                                                        rules={[
                                                            {
                                                                required: maintenanceServiceUnit?.service?.miles,
                                                                message: messages['required_field']
                                                            },
                                                            {
                                                                min: parseFloat(formData.engineOdometer || 1),
                                                                message: `${messages['completeMaintenanceService_error_next_odometer']} ${parseFloat(formData.engineOdometer || 1)}`
                                                            }
                                                        ]}
                                                    >
                                                        <InputNumeric
                                                            id="nextOdometer"
                                                            type="number"
                                                            placeholder=""
                                                            maxLength={15}
                                                            onChange={(value) => setFormData({ ...formData, nextOdometer: parseInt(value) })}
                                                            onlyPositive
                                                        />
                                                    </Form.Item>
                                                    : <></>
                                            }
                                            {
                                                maintenanceServiceUnit?.service?.engineHours ?
                                                    <Form.Item
                                                        name="nextEngineHours"
                                                        label={messages['completeMaintenanceService_next_engine_hours']}
                                                        rules={[
                                                            {
                                                                required: maintenanceServiceUnit?.service?.engineHours,
                                                                message: messages['required_field']
                                                            },
                                                            {
                                                                min: parseFloat(formData.engineHours || 1),
                                                                message: `${messages['completeMaintenanceService_error_next_engine_hours']} ${parseFloat(formData.engineHours || 1)}`
                                                            }
                                                        ]}
                                                    >
                                                        <InputNumeric
                                                            id="nextEngineHours"
                                                            type="number"
                                                            placeholder="$"
                                                            maxLength={8}
                                                            onChange={(value) => setFormData({ ...formData, nextEngineHours: value })}
                                                            onlyPositive
                                                        />
                                                    </Form.Item>
                                                    : <></>
                                            }
                                        </>
                                    }
                                </>
                            )
                        }
                        <Form.Item
                            name="attachments"
                            label={messages['completeMaintenanceService_attachment']}
                        >
                            <FileUpload
                                id="attachments"
                                value={formData?.attachments}
                                placeholder={messages['completeMaintenanceService_select_files']}
                                types="image/*,.txt,.doc,.docx,.pdf,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                maxSize={4}
                                maxSizeMessage={`${messages['max']}: 4MB`}
                                helpText={messages['attachmentsFilesAllowed']}
                                uploadAttachment={(data) => { dispatch(uploadAttachment(data)) }}
                                uploadedAttachment={uploadedAttachment}
                                loadingUploadFile={loadingUploadFile}
                                onChange={(value) => setFormData({ ...formData, attachments: value })}
                            />
                        </Form.Item>
                        <Form.Item
                            name="notes"
                            label={messages['completeMaintenanceService_max_notes']}
                        >
                            <TextArea
                                id="notes"
                                placeholder={messages['completeMaintenanceService_notes']}
                                type="textarea"
                                maxLength={250}
                                value={formData.notes || ''}
                                onChange={(value) => setFormData({ ...formData, notes: value })}
                            />
                        </Form.Item>
                    </Form>
                </div>
                {
                    errorMessage &&
                    <div style={{ padding: "5px 5px 9px 20px" }}>
                        <small className="text-danger" style={{ paddingTop: 5 }}>
                            {errorMessage}
                        </small>
                    </div>
                }
            </Preferences>
            <NotificationContainer />
        </Modal>
    )
}

export default CompleteMaintenanceService;